import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';

interface Inspector {
  id: string;
  display_name: string;
  is_admin: boolean;
  created_at: string;
}

interface InspectorManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InspectorManagerModal({ isOpen, onClose }: InspectorManagerModalProps) {
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPin, setNewPin] = useState('');
  const [editingPin, setEditingPin] = useState<string | null>(null);
  const [editPinValue, setEditPinValue] = useState('');
  const { showToast } = useToast();
  const { inspector: currentUser } = useAuth();

  const loadInspectors = async () => {
    try {
      const data = await api.getInspectors();
      setInspectors(data.inspectors);
    } catch {
      showToast('Failed to load inspectors');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadInspectors();
      setNewName('');
      setNewPin('');
      setEditingPin(null);
      setEditPinValue('');
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!newName.trim()) {
      showToast('Name is required');
      return;
    }
    if (newPin.length < 4) {
      showToast('PIN must be at least 4 digits');
      return;
    }
    setIsLoading(true);
    try {
      await api.register(newPin, newName.trim());
      showToast(`${newName.trim()} added`);
      setNewName('');
      setNewPin('');
      await loadInspectors();
    } catch (err: any) {
      showToast(`Failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePin = async (id: string) => {
    if (editPinValue.length < 4) {
      showToast('PIN must be at least 4 digits');
      return;
    }
    setIsLoading(true);
    try {
      await api.changePin(id, editPinValue);
      showToast('PIN updated');
      setEditingPin(null);
      setEditPinValue('');
    } catch (err: any) {
      showToast(`Failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAdmin = async (id: string, name: string, currentStatus: boolean) => {
    const action = currentStatus ? `Demote "${name}" from admin?` : `Promote "${name}" to admin?`;
    if (!window.confirm(action)) return;
    setIsLoading(true);
    try {
      const result = await api.toggleAdmin(id);
      showToast(`${name} is now ${result.is_admin ? 'admin' : 'regular user'}`);
      await loadInspectors();
    } catch (err: any) {
      showToast(`Failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Remove inspector "${name}"?`)) return;
    setIsLoading(true);
    try {
      await api.deleteInspector(id);
      showToast(`${name} removed`);
      await loadInspectors();
    } catch (err: any) {
      showToast(`Failed: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal id="inspector-manager" isOpen={isOpen} onClose={onClose} title="Manage Inspectors" size="md">
      <div className="space-y-4">
        {currentUser?.is_admin ? (
          <div className="p-4 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl">
            <h3 className="text-sm font-bold mb-3">Add New Inspector</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Full name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">PIN (4-6 digits)</label>
                <input
                  type="password"
                  className="input-field font-mono"
                  placeholder="123456"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" onClick={handleAdd} disabled={isLoading} className="w-full justify-center">
                  Add
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl text-center">
            <p className="text-sm text-[var(--text-muted)]">
              Only admins can manage inspectors. Contact an admin to add or change accounts.
            </p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-bold mb-3">Current Inspectors ({inspectors.length})</h3>
          <div className="space-y-2">
            {inspectors.map((insp) => (
              <div
                key={insp.id}
                className="flex items-center justify-between p-3 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-xl flex-wrap gap-2"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[var(--accent-light)] border border-[var(--accent-color)] flex items-center justify-center text-xs font-bold text-[var(--accent-color)]">
                    {insp.display_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-bold">
                      {insp.display_name}
                      {insp.is_admin && (
                        <Badge variant="accent" className="ml-2 text-[10px]">admin</Badge>
                      )}
                      {insp.id === currentUser?.id && (
                        <Badge variant="info" className="ml-2 text-[10px]">you</Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-[var(--text-dim)] font-mono">{insp.id}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {currentUser?.is_admin ? (
                    editingPin === insp.id ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="password"
                        className="input-field text-xs font-mono w-24"
                        placeholder="New PIN"
                        maxLength={6}
                        value={editPinValue}
                        onChange={(e) => setEditPinValue(e.target.value.replace(/\D/g, ''))}
                        autoFocus
                      />
                      <Button variant="primary" size="sm" onClick={() => handleChangePin(insp.id)} disabled={isLoading}>
                        Save
                      </Button>
                      <button
                        className="text-xs text-[var(--text-dim)] hover:text-[var(--text-main)]"
                        onClick={() => { setEditingPin(null); setEditPinValue(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        className="text-xs text-[var(--text-dim)] hover:text-[var(--accent-color)] font-bold transition-colors"
                        onClick={() => setEditingPin(insp.id)}
                      >
                        Change PIN
                      </button>
                      {insp.id !== currentUser?.id && (
                        <button
                          className={`text-xs font-bold transition-colors ${
                            insp.is_admin
                              ? 'text-[var(--text-muted)] hover:text-[var(--text-dim)]'
                              : 'text-[var(--text-muted)] hover:text-[var(--accent-color)]'
                          }`}
                          onClick={() => handleToggleAdmin(insp.id, insp.display_name, insp.is_admin)}
                          disabled={isLoading}
                        >
                          {insp.is_admin ? 'Demote' : 'Promote'}
                        </button>
                      )}
                      {insp.id !== currentUser?.id && (
                        <button
                          className="text-xs text-[var(--text-dim)] hover:text-[var(--status-ng-text)] font-bold transition-colors"
                          onClick={() => handleDelete(insp.id, insp.display_name)}
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </>
                  )
                ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-[var(--border-color)]">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
